import { useState } from "react";
import type { CardAnswers } from "./CardForm";
import type { NewDealAnswers } from "./NewDealForm";
import ResultActions from "./ResultActions";

export type ResultStatus = "pass" | "block" | "conditional" | "info";

export interface ResultItem {
  label: string;
  status: ResultStatus;
  message: string;
  reason: string;
  resolve?: string;
}

// 의미색은 대표색(brand)과 분리합니다.
// 통과=초록 / 확인 필요=주의(amber) / 제한=차단(red)
// 다크모드에서는 계열을 유지하되 어두운 배경 + 밝은 글자로 짝지어 줍니다.
const statusMeta: Record<
  ResultStatus,
  { label: string; icon: string; badge: string; stripe: string; iconColor: string; iconBg: string; text: string }
> = {
  pass: {
    label: "통과",
    icon: "ri-checkbox-circle-fill",
    badge: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    stripe: "border-l-green-500",
    iconColor: "text-green-600 dark:text-green-400",
    iconBg: "bg-green-50 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-300",
  },
  conditional: {
    label: "확인 필요",
    icon: "ri-error-warning-fill",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    stripe: "border-l-amber-500",
    iconColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-50 dark:bg-amber-900/30",
    text: "text-amber-700 dark:text-amber-300",
  },
  block: {
    label: "제한",
    icon: "ri-close-circle-fill",
    badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    stripe: "border-l-red-500",
    iconColor: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-50 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-300",
  },
  info: {
    label: "참고",
    icon: "ri-information-fill",
    badge: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
    stripe: "border-l-gray-400",
    iconColor: "text-gray-500 dark:text-gray-400",
    iconBg: "bg-gray-50 dark:bg-gray-800",
    text: "text-gray-600 dark:text-gray-300",
  },
};

export function calculateCardResults(answers: CardAnswers): ResultItem[] {
  const items: ResultItem[] = [];

  items.push({
    label: "공무원 신분",
    status: answers.공무원 === "예" ? "block" : "pass",
    message:
      answers.공무원 === "예"
        ? "공무원은 국민내일배움카드 발급 제외 대상입니다."
        : "공무원 신분이 아닙니다.",
    reason:
      answers.공무원 === "예" ? "「공무원입니다」 선택" : "「아닙니다」 선택",
    resolve:
      answers.공무원 === "예"
        ? "공무원 신분은 법령상 해소 방법이 없습니다. 퇴직 후 재신청을 검토해 주세요."
        : undefined,
  });

  const age = parseInt(answers.카드나이, 10);
  const ageBlocked = !isNaN(age) && age >= 75;
  items.push({
    label: "연령",
    status: ageBlocked ? "block" : "pass",
    message: ageBlocked
      ? "만 75세 이상이면 발급 제한 대상입니다."
      : `만 ${age}세 — 연령 요건을 충족합니다.`,
    reason: !isNaN(age) ? `만 ${age}세 입력` : "나이 미입력",
    resolve: ageBlocked
      ? "연령 상한은 해소되지 않습니다. 지자체 평생교육 등 대안 프로그램을 확인해 주세요."
      : undefined,
  });

  items.push({
    label: "부정수급 제재",
    status: answers.부정수급 === "예" ? "block" : "pass",
    message:
      answers.부정수급 === "예"
        ? "부정수급 제재기간 중에는 카드 발급이 제한됩니다."
        : "부정수급 제재 이력이 없습니다.",
    reason:
      answers.부정수급 === "예"
        ? "「제재 기간 중이거나 최근에 있었습니다」 선택"
        : "「없습니다」 선택",
    resolve:
      answers.부정수급 === "예"
        ? "제재 기간이 끝난 뒤 재신청하세요. 기간은 관할 고용센터에서 확인할 수 있습니다."
        : undefined,
  });

  return items;
}

const 근로Labels: Record<string, string> = {
  미취업: "일하고 있지 않습니다",
  프리랜서: "프리랜서입니다",
  단시간: "주 30시간 미만 단시간 근로",
  플랫폼: "플랫폼 노동·일용직",
  재직: "주 30시간 이상 + 고용보험 가입",
};

const 사업자Labels: Record<string, string> = {
  없음: "없습니다",
  휴면: "등록만 있고 미영업",
  영업: "등록 있고 영업 중",
};

const 학적Labels: Record<string, string> = {
  없음: "학생이 아닙니다",
  졸업예정: "대학(원) 마지막 학기 등록",
  휴학: "휴학 중",
  학점충족: "졸업 이수학점 취득",
  재학: "대학(원) 재학 중",
  사이버대: "사이버대·방통대 재학",
  학점은행: "학점은행제",
  고교직업: "특성화고·마이스터고 3-2",
  고교일반: "일반 고등학교 재학",
};

const 타훈련Labels: Record<string, string> = {
  없음: "없음",
  개강전종료: "개시일 전 종료",
  중복: "기간 겹침",
};

const 이력Labels: Record<string, string> = {
  없음: "없음",
  "1회": "올해 한 번",
  "2회": "올해 두 번 이상",
  동일재참여: "동일 아카데미 재지원",
  동시: "다른 아카데미 참여 중",
};

const 수급Labels: Record<string, string> = {
  없음: "해당 없음",
  실업급여: "구직급여 수급 중",
  국취제: "국민취업지원제도 참여 중",
};

export function calculateNewDealResults(answers: NewDealAnswers): ResultItem[] {
  const items: ResultItem[] = [];

  items.push({
    label: "국적",
    status: answers.국적 === "외국" ? "block" : "pass",
    message:
      answers.국적 === "외국"
        ? "대한민국 국적 보유가 참여 요건입니다."
        : "대한민국 국적을 보유하고 있습니다.",
    reason:
      answers.국적 === "외국"
        ? "「대한민국 국적이 아닙니다」 선택"
        : "「대한민국 국적입니다」 선택",
    resolve:
      answers.국적 === "외국"
        ? "대한민국 국적 취득 후 재신청하세요. 국적 요건은 해소 방법이 없습니다."
        : undefined,
  });

  const age = parseInt(answers.나이, 10);
  const maxAge = answers.군필 ? 39 : 34;
  const ageOut = !isNaN(age) && (age < 15 || age > maxAge);
  items.push({
    label: "연령",
    status: ageOut ? "block" : "pass",
    message: ageOut
      ? `만 ${age}세 — 참여 연령 요건(만 15세 ~ ${maxAge}세)을 벗어납니다.`
      : `만 ${age}세 — 참여 연령 요건(만 15세 ~ ${maxAge}세)을 충족합니다.`,
    reason: !isNaN(age)
      ? `만 ${age}세 입력${answers.군필 ? " · 군필 반영" : ""}`
      : "나이 미입력",
    resolve: ageOut
      ? "연령 상한은 해소되지 않습니다. 대상 연령대가 아니면 다른 과정을 확인해 주세요."
      : undefined,
  });

  const 근로Status: ResultStatus =
    answers.근로 === "재직"
      ? "block"
      : answers.근로 === "미취업"
      ? "pass"
      : "conditional";
  items.push({
    label: "취업 상태",
    status: 근로Status,
    message:
      answers.근로 === "재직"
        ? "고용보험 피보험자격으로 취업 중이면 참여가 제한됩니다."
        : answers.근로 === "미취업"
        ? "미취업 상태입니다."
        : "세부 근로 형태에 따라 참여 가능 여부가 달라질 수 있습니다.",
    reason: answers.근로
      ? `「${근로Labels[answers.근로] ?? answers.근로}」 선택`
      : "미선택",
    resolve:
      answers.근로 === "재직"
        ? "고용보험 피보험자격 상실(퇴사) 후 참여 가능합니다. 이직·퇴직 시점 기준으로 재확인하세요."
        : answers.근로 === "미취업"
        ? undefined
        : "근로 형태별로 판정이 다릅니다. 고용센터에서 고용보험 피보험자격을 확인해 주세요.",
  });

  const 사업자Status: ResultStatus =
    answers.사업자 === "영업"
      ? "block"
      : answers.사업자 === "휴면"
      ? "conditional"
      : "pass";
  items.push({
    label: "사업자등록",
    status: 사업자Status,
    message:
      answers.사업자 === "영업"
        ? "실제 영업 중인 사업자등록이 있으면 참여가 제한될 수 있습니다."
        : answers.사업자 === "휴면"
        ? "휴면 상태의 사업자등록은 추가 확인이 필요합니다."
        : "사업자등록이 없습니다.",
    reason: answers.사업자
      ? `「${사업자Labels[answers.사업자] ?? answers.사업자}」 선택`
      : "미선택",
    resolve:
      answers.사업자 === "영업"
        ? "폐업신고 후 참여 가능합니다. 실제 영업 여부는 고용센터에서 확인합니다."
        : answers.사업자 === "휴면"
        ? "실제 영업 여부가 관건입니다. 고용센터에서 휴면 사업자 여부를 확인해 주세요."
        : undefined,
  });

  const 학적Status: ResultStatus =
    answers.학적 === "재학" || answers.학적 === "고교일반"
      ? "block"
      : answers.학적 === "사이버대" || answers.학적 === "학점은행"
      ? "conditional"
      : "pass";
  items.push({
    label: "학적",
    status: 학적Status,
    message:
      answers.학적 === "재학"
        ? "대학(원) 재학 중이면 참여가 제한됩니다."
        : answers.학적 === "고교일반"
        ? "일반 고등학교 재학 중이면 참여가 제한됩니다."
        : answers.학적 === "사이버대" || answers.학적 === "학점은행"
        ? "해당 학적 상태는 추가 확인이 필요합니다."
        : "학적 요건을 충족합니다.",
    reason: answers.학적
      ? `「${학적Labels[answers.학적] ?? answers.학적}」 선택`
      : "미선택",
    resolve:
      answers.학적 === "재학" || answers.학적 === "고교일반"
        ? "재학 중에는 참여가 제한됩니다. 졸업·휴학·수료 후 재확인해 주세요."
        : answers.학적 === "사이버대" || answers.학적 === "학점은행"
        ? "사이버대·학점은행은 과정별로 판정이 다릅니다. 운영기관에 문의해 주세요."
        : undefined,
  });

  items.push({
    label: "구직등록",
    status: answers.구직등록 === "안함" ? "block" : "pass",
    message:
      answers.구직등록 === "안함"
        ? "고용24(워크넷) 구직등록이 참여 요건입니다."
        : "구직등록을 완료했습니다.",
    reason:
      answers.구직등록 === "안함"
        ? "「안 했거나 잘 모르겠습니다」 선택"
        : "「했습니다」 선택",
    resolve:
      answers.구직등록 === "안함"
        ? "고용24(work24.go.kr)에서 구직등록하세요. 온라인 5분, 무료입니다."
        : undefined,
  });

  items.push({
    label: "타훈련 중복",
    status: answers.타훈련 === "중복" ? "block" : "pass",
    message:
      answers.타훈련 === "중복"
        ? "기간이 겹치는 다른 훈련이 있으면 참여가 제한됩니다."
        : answers.타훈련 === "개강전종료"
        ? "아카데미 개시일 전에 종료되면 중복되지 않습니다."
        : "중복되는 훈련이 없습니다.",
    reason: answers.타훈련
      ? `「${타훈련Labels[answers.타훈련] ?? answers.타훈련}」 선택`
      : "미선택",
    resolve:
      answers.타훈련 === "중복"
        ? "기간이 겹치는 훈련이 끝난 뒤 참여하세요. 개시일 전 종료면 중복되지 않습니다."
        : undefined,
  });

  const 이력Status: ResultStatus =
    answers.이력 === "2회" || answers.이력 === "동일재참여" || answers.이력 === "동시"
      ? "block"
      : answers.이력 === "1회"
      ? "conditional"
      : "pass";
  items.push({
    label: "참여 이력",
    status: 이력Status,
    message:
      answers.이력 === "2회"
        ? "올해 두 번 이상 참여하면 추가 참여가 제한됩니다."
        : answers.이력 === "동일재참여"
        ? "동일 아카데미 재참여는 제한됩니다."
        : answers.이력 === "동시"
        ? "다른 아카데미 동시 참여는 불가합니다."
        : answers.이력 === "1회"
        ? "올해 한 번 참여했으므로 다른 아카데미는 가능할 수 있습니다."
        : "참여 이력이 없습니다.",
    reason: answers.이력
      ? `「${이력Labels[answers.이력] ?? answers.이력}」 선택`
      : "미선택",
    resolve:
      answers.이력 === "2회" || answers.이력 === "동일재참여" || answers.이력 === "동시"
        ? "올해 참여 횟수와 동일 과정 여부를 고용센터에서 확인하세요. 다음 해 재신청이 가능할 수 있습니다."
        : answers.이력 === "1회"
        ? "다른 아카데미 1회 참여는 가능할 수 있습니다. 고용센터에서 최종 확인하세요."
        : undefined,
  });

  items.push({
    label: "다른 지원제도 수급",
    status: "info",
    message:
      answers.수급 === "실업급여"
        ? "구직급여 수급 중이면 참여수당과 중복 여부를 확인해야 합니다."
        : answers.수급 === "국취제"
        ? "국민취업지원제도 참여 중이면 수당 중복 여부를 확인해야 합니다."
        : "다른 지원제도 수급 이력이 없습니다.",
    reason: answers.수급
      ? `「${수급Labels[answers.수급] ?? answers.수급}」 선택`
      : "미선택",
    resolve:
      answers.수급 === "실업급여" || answers.수급 === "국취제"
        ? "참여수당과의 중복 여부는 상담에서 확인해 드립니다."
        : undefined,
  });

  return items;
}

interface ResultSectionProps {
  system: "카드" | "뉴딜";
  items: ResultItem[];
  onReset: () => void;
}

export default function ResultSection({ system, items, onReset }: ResultSectionProps) {
  const [showDetails, setShowDetails] = useState(false);

  const hasBlock = items.some((i) => i.status === "block");
  const hasConditional = items.some((i) => i.status === "conditional");

  const passCount = items.filter((i) => i.status === "pass").length;
  const condCount = items.filter((i) => i.status === "conditional").length;
  const blockCount = items.filter((i) => i.status === "block").length;

  const conclusionLabel = hasBlock ? "어려움" : hasConditional ? "조건부" : "가능";
  const headline = hasBlock
    ? system === "카드"
      ? "지금은 카드 발급이 어렵습니다"
      : "지금은 참여가 어렵습니다"
    : hasConditional
    ? "추가 확인이 필요한 항목이 있습니다"
    : system === "카드"
    ? "카드 발급 요건을 충족합니다"
    : "참여 요건을 충족합니다";

  const bottleneck =
    items.find((i) => i.status === "block") ||
    items.find((i) => i.status === "conditional");

  const blockLabels = items.filter((i) => i.status === "block").map((i) => i.label);
  const condLabels = items
    .filter((i) => i.status === "conditional")
    .map((i) => i.label);

  const reasonSummary = hasBlock
    ? `${blockLabels.join(", ")} 항목이 제한 조건에 해당합니다. ${bottleneck?.resolve ?? "아래 상세를 확인해 주세요."}`
    : hasConditional
    ? `${condLabels.join(", ")} 항목은 세부 조건에 따라 결과가 달라질 수 있어 확인이 필요합니다.`
    : "제출하신 답변 기준으로 모든 항목이 요건을 충족합니다. 신청 절차를 진행하시면 됩니다.";

  const hero = hasBlock
    ? { solid: "bg-red-600", icon: "ri-close-circle-fill" }
    : hasConditional
    ? { solid: "bg-amber-600", icon: "ri-error-warning-fill" }
    : { solid: "bg-green-600", icon: "ri-checkbox-circle-fill" };

  const counts = [
    { n: passCount, label: "통과", icon: "ri-checkbox-circle-fill", block: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300" },
    { n: condCount, label: "확인 필요", icon: "ri-error-warning-fill", block: "bg-amber-50 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-300" },
    { n: blockCount, label: "제한", icon: "ri-close-circle-fill", block: "bg-red-50 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300" },
  ];

  return (
    <div className="space-y-6">
      {/* 판정 결론 — 가장 크게 */}
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
        <div className={`px-5 py-8 md:px-7 md:py-10 ${hero.solid}`}>
          <div className="flex items-start gap-4 md:gap-5">
            <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/25 shrink-0">
              <i className={`${hero.icon} text-3xl md:text-4xl text-white`} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm font-semibold text-white/90 uppercase tracking-wider">
                {conclusionLabel}
              </p>
              <h2 className="text-2xl md:text-4xl font-bold text-white mt-2 leading-tight break-keep">
                {headline}
              </h2>
            </div>
          </div>
        </div>

        {/* 통과 · 확인 필요 · 제한 개수 */}
        <div className="grid grid-cols-3 bg-white dark:bg-gray-900" role="list" aria-label="판정 개수 요약">
          {counts.map((c) => (
            <div key={c.label} className={`px-2 py-4 text-center ${c.block}`}>
              <div className={`flex items-center justify-center gap-1.5 text-3xl font-bold ${c.text}`}>
                <i className={`${c.icon} text-lg`} aria-hidden="true" />
                {c.n}
              </div>
              <div className={`text-xs font-semibold mt-0.5 ${c.text}`}>{c.label}</div>
              <span className="sr-only">{c.label} {c.n}건</span>
            </div>
          ))}
        </div>

        {/* 이유 요약 */}
        <div className="bg-white border-t border-gray-200 px-5 py-4 md:px-7 md:py-5 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm md:text-base text-gray-700 leading-relaxed break-keep dark:text-gray-300">
            {reasonSummary}
          </p>
        </div>
      </div>

      {/* 항목별 상세 — 접었다 펼치기 */}
      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          className="w-full flex items-center justify-between px-4 py-3.5 md:px-5 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
            항목별 상세
          </span>
          <i
            className={`${showDetails ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg text-gray-500 dark:text-gray-400`}
            aria-hidden="true"
          />
        </button>
        {showDetails && (
          <div className="px-4 pb-4 md:px-5 space-y-3">
            {items.map((item, idx) => {
              const meta = statusMeta[item.status];
              return (
                <div
                  key={idx}
                  className={`rounded-lg border border-gray-200 bg-white border-l-4 dark:border-gray-800 dark:bg-gray-900 ${meta.stripe}`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 ${meta.iconBg}`}>
                        <i className={`${meta.icon} ${meta.iconColor} text-lg`} aria-hidden="true" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="sr-only">상태: {meta.label}</span>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-900 break-keep dark:text-gray-100">{item.label}</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${meta.badge}`}>
                            {meta.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 leading-relaxed break-keep dark:text-gray-300">
                          {item.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed break-keep dark:text-gray-400">
                          <span className="font-semibold text-gray-600 dark:text-gray-300">판정 근거 </span>
                          {item.reason}
                        </p>
                        {item.resolve && (
                          <div className="mt-2.5 bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800">
                            <p className="text-sm text-gray-800 leading-relaxed break-keep dark:text-gray-200">
                              <span className="font-bold text-gray-900 dark:text-gray-100">다음 행동 </span>
                              {item.resolve}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 저장·공유 */}
      <ResultActions system={system} headline={headline} items={items} />

      <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4 leading-relaxed break-keep dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300">
        이 판정은 이 브라우저 안에서만 계산됐습니다. 입력하신 답변은 전송되지도, 저장되지도 않았습니다.
      </p>

      <button
        type="button"
        onClick={onReset}
        className="w-full py-3.5 px-4 rounded-lg text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-900"
      >
        답변 고치기
      </button>
    </div>
  );
}